import { useState, useCallback, useRef, useEffect } from 'react';
import { cloneDeep, isEqual } from 'lodash';

interface UseUndoRedoOptions<T> {
  maxHistory?: number;
  debounceDelay?: number;
  onUndo?: (state: T) => void;
  onRedo?: (state: T) => void;
}

export function useUndoRedo<T>(
  initialState: T,
  options: UseUndoRedoOptions<T> = {}
) {
  const {
    maxHistory = 50,
    debounceDelay = 500,
    onUndo,
    onRedo,
  } = options;

  const [history, setHistory] = useState<T[]>([cloneDeep(initialState)]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Get current state
  const currentState = history[currentIndex];

  // Check if can undo/redo
  const canUndo = currentIndex > 0;
  const canRedo = currentIndex < history.length - 1;

  // Add new state to history
  const addToHistory = useCallback((newState: T) => {
    // Clear any pending debounce
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      setHistory(prevHistory => {
        // Don't add if it's the same as current state
        if (isEqual(newState, prevHistory[currentIndex])) {
          return prevHistory;
        }

        // Remove any states after current index (branching)
        const newHistory = prevHistory.slice(0, currentIndex + 1);
        
        // Add new state
        newHistory.push(cloneDeep(newState));

        // Limit history size
        if (newHistory.length > maxHistory) {
          newHistory.shift();
        } else {
          setCurrentIndex(prev => prev + 1);
        }

        return newHistory;
      });
    }, debounceDelay);
  }, [currentIndex, maxHistory, debounceDelay]);

  // Undo action
  const undo = useCallback(() => {
    if (canUndo) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      onUndo?.(history[newIndex]);
      return history[newIndex];
    }
    return currentState;
  }, [canUndo, currentIndex, history, currentState, onUndo]);

  // Redo action
  const redo = useCallback(() => {
    if (canRedo) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      onRedo?.(history[newIndex]);
      return history[newIndex];
    }
    return currentState;
  }, [canRedo, currentIndex, history, currentState, onRedo]);

  // Reset history
  const reset = useCallback((newState?: T) => {
    const resetState = newState || initialState;
    setHistory([cloneDeep(resetState)]);
    setCurrentIndex(0);
  }, [initialState]);

  // Clear history but keep current state
  const clearHistory = useCallback(() => {
    setHistory([cloneDeep(currentState)]);
    setCurrentIndex(0);
  }, [currentState]);

  // Get history info
  const getHistoryInfo = useCallback(() => {
    return {
      size: history.length,
      currentIndex,
      canUndo,
      canRedo,
      undoSteps: currentIndex,
      redoSteps: history.length - currentIndex - 1,
    };
  }, [history.length, currentIndex, canUndo, canRedo]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl/Cmd + Z for undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      }
      // Ctrl/Cmd + Shift + Z or Ctrl/Cmd + Y for redo
      else if (
        ((e.ctrlKey || e.metaKey) && e.key === 'z' && e.shiftKey) ||
        ((e.ctrlKey || e.metaKey) && e.key === 'y')
      ) {
        e.preventDefault();
        redo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  return {
    state: currentState,
    setState: addToHistory,
    undo,
    redo,
    canUndo,
    canRedo,
    reset,
    clearHistory,
    historyInfo: getHistoryInfo(),
  };
}
